/* eslint-disable arrow-body-style */
/* eslint-disable eqeqeq */
import { LoadingButton } from '@mui/lab';
import { Button, Card, Divider, InputAdornment, Stack, Typography } from '@mui/material';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Item, ItemTypesEnum, MCQQuestion, QuestionTypesEnum, QuizItem } from 'src/@types/Course';
import Iconify from 'src/components/Iconify';
import { parseQuestionsFile, downloadQuestionsTemplate } from './importQuestions';
import { AddQuestion } from './components/AddQuestion';
import { MCQ, TrueFalse } from './components/Question';
import { QuizItemDefaultValues, QuizSchema } from '../components/sectionItemsSchemas';
// ----------------------------------------------------------------------

export default function QuizItemForm({
  onSubmit,
  currentItem,
}: {
  onSubmit(data: Item): void;
  currentItem: Item | undefined;
}) {
  const methods = useForm({
    resolver: yupResolver(QuizSchema),
    defaultValues: QuizItemDefaultValues,
  });
  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const values = watch();
  const { enqueueSnackbar } = useSnackbar();

  const updateDefaultValues = () => {
    return {
      title: currentItem?.title || '',
      title_ar: currentItem?.title_ar || '',
      description: currentItem?.description || '',
      description_ar: currentItem?.description_ar || '',
      details: {
        questions: (currentItem?.details as QuizItem)?.questions || [],
        type: ItemTypesEnum.QUIZ,
        attemptsLimit: (currentItem?.details as QuizItem)?.attemptsLimit || 3,
        secondsPerQuestion: (currentItem?.details as QuizItem)?.secondsPerQuestion ?? 0,
        passingScore: (currentItem?.details as QuizItem)?.passingScore ?? 50,
        questionsToServe: (currentItem?.details as QuizItem)?.questionsToServe ?? 0,
      } as QuizItem,
      duration: currentItem?.duration || 0,
      // Kept as stored (the server also keeps every key the form does not send).
      ...(currentItem?.isFreeToWatch !== undefined
        ? { isFreeToWatch: currentItem.isFreeToWatch }
        : {}),
      ...(currentItem?.points !== undefined ? { points: currentItem.points } : {}),
    };
  };
  useEffect(() => {
    if (currentItem) {
      setQuestions((currentItem.details as QuizItem).questions ?? []);
      reset(updateDefaultValues());
    }
    if (!currentItem) {
      reset(QuizItemDefaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem]);
  const [questions, setQuestions] = useState<MCQQuestion[]>([]);

  const setQuestionType = (index: number, type: QuestionTypesEnum) => {
    const details = { ...values.details } as QuizItem;
    if (details.questions) {
      details.questions[index].type = type;
    }
    setValue('details', details);
  };

  const DeleteQuestion = (index: number) => {
    const details = { ...values.details } as QuizItem;
    if (details.questions) {
      details.questions.splice(index, 1);
    }
    setValue('details', details);
  };

  const addQuestion = (type: QuestionTypesEnum) => {
    questions.push({ answers: [], multi: false, title: '', type });
    setQuestions([...questions]);
  };

  const deleteQuestionAt = (index: number) => {
    questions.splice(index, 1);
    setQuestions([...questions]);
    DeleteQuestion(index);
  };

  // #2: import questions from a CSV/JSON file → append to the quiz.
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = await parseQuestionsFile(file);
      if (!imported.length) {
        enqueueSnackbar('No questions found in the file', { variant: 'warning' });
        return;
      }
      const merged = [...questions, ...imported];
      setQuestions(merged);
      setValue('details', { ...(values.details as QuizItem), questions: merged });
      enqueueSnackbar(`Imported ${imported.length} question(s)`);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not parse the file', { variant: 'error' });
    } finally {
      e.target.value = '';
    }
  };

  // #2 quiz export: download the current questions (or a sample if empty) as a
  // CSV in the FULL import layout, so it can be edited offline + re-imported.
  const csvCell = (v: any) => {
    const s = String(v ?? '');
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExportTemplate = () => {
    // Empty quiz -> download the unified website-wide template. With questions ->
    // export the current ones (old FULL layout, still round-trips via import).
    const liveQs = (values?.details as QuizItem)?.questions;
    if (!(liveQs?.length || questions.length)) {
      downloadQuestionsTemplate();
      return;
    }
    const headers = [
      'question',
      'question_ar',
      'description',
      'description_ar',
      'answer',
      'answer_ar',
      'points',
      'minusPoints',
      'correct',
    ];
    // Prefer the live form state (edited in the question cards), else the list.
    const live = (values?.details as QuizItem)?.questions;
    const source: MCQQuestion[] =
      (live && live.length ? live : questions.length ? questions : []) || [];
    const sample: MCQQuestion[] = [
      {
        title: 'Sample — what is 2 + 2?',
        title_ar: 'مثال — كم حاصل ٢ + ٢؟',
        multi: false,
        type: QuestionTypesEnum.MCQ,
        answers: [
          { name: '3', points: 0, minusPoints: 0, correct: false },
          { name: '4', points: 1, minusPoints: 0, correct: true },
          { name: '5', points: 0, minusPoints: 0, correct: false },
        ] as any,
      } as MCQQuestion,
    ];
    const list = source.length ? source : sample;
    const rows: string[][] = [headers];
    list.forEach((q: any) => {
      const answers: any[] = q.answers || [];
      if (!answers.length) {
        rows.push([q.title || '', q.title_ar || '', q.description || '', q.description_ar || '', '', '', '', '', '']);
        return;
      }
      answers.forEach((a) => {
        rows.push([
          q.title || '',
          q.title_ar || '',
          q.description || '',
          q.description_ar || '',
          a.name || '',
          a.name_ar || '',
          String(a.points ?? (a.correct ? 1 : 0)),
          String(a.minusPoints ?? 0),
          a.correct ? 'true' : 'false',
        ]);
      });
    });
    const csv = rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
    // Prepend a BOM so Excel opens Arabic correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = source.length ? 'quiz-questions.csv' : 'quiz-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    enqueueSnackbar(
      source.length ? `Exported ${list.length} question(s)` : 'Downloaded a blank template'
    );
  };

  return (
    <Stack height={'100%'} sx={{ overflow: 'auto', px: 0.5 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {/* ---- quiz details ---- */}
          <Card variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Quiz details
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <RHFTextField fullWidth name="title" label="Quiz title (English)" />
              <RHFTextField fullWidth name="title_ar" label="Quiz title (Arabic)" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <RHFTextField
                type="number"
                name="duration"
                label="Time limit"
                sx={{ maxWidth: 220 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">min</InputAdornment>,
                }}
              />
              <RHFTextField
                type="number"
                name="details.attemptsLimit"
                label="Attempts allowed"
                sx={{ maxWidth: 220 }}
                helperText="How many times a student may take this quiz"
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <RHFTextField
                type="number"
                name="details.passingScore"
                label="Passing score"
                sx={{ maxWidth: 220 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="Min % to pass this quiz"
              />
              <RHFTextField
                type="number"
                name="details.secondsPerQuestion"
                label="Per-question time"
                sx={{ maxWidth: 220 }}
                InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                helperText="0 = no per-question limit"
              />
              <RHFTextField
                type="number"
                name="details.questionsToServe"
                label="Questions per attempt"
                sx={{ maxWidth: 220 }}
                helperText="0 = serve all (else random subset)"
              />
            </Stack>
          </Card>

          {/* ---- questions ---- */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Typography variant="h6">
              Questions{' '}
              <Typography component="span" variant="h6" sx={{ color: 'text.disabled' }}>
                ({questions.length})
              </Typography>
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={handleExportTemplate}
                startIcon={<Iconify icon="eva:download-outline" />}
              >
                {(values?.details as QuizItem)?.questions?.length || questions.length
                  ? 'Export'
                  : 'Template'}
              </Button>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={
                  <Iconify icon="eva:upload-outline" sx={{ transform: 'rotate(180deg)' }} />
                }
              >
                Import
                <input hidden type="file" accept=".csv,.json,.txt" onChange={handleImportFile} />
              </Button>
              <AddQuestion onClick={addQuestion} />
            </Stack>
          </Stack>

          {questions.length === 0 ? (
            <Card
              variant="outlined"
              sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}
            >
              <Iconify
                icon="eva:clipboard-outline"
                width={44}
                height={44}
                sx={{ color: 'text.disabled', mb: 1 }}
              />
              <Typography color="text.secondary">
                No questions yet. Click <b>Add question</b> to create one.
              </Typography>
            </Card>
          ) : (
            <Stack>
              {questions.map((item, index) => {
                if (item.type === QuestionTypesEnum.trueFalse) {
                  return (
                    <TrueFalse
                      key={index}
                      watch={watch}
                      setValue={setValue}
                      setQuestionType={setQuestionType}
                      index={index}
                      onDelete={() => deleteQuestionAt(index)}
                    />
                  );
                }
                return (
                  <MCQ
                    key={index}
                    watch={watch}
                    setValue={setValue}
                    setQuestionType={setQuestionType}
                    title={item.type === QuestionTypesEnum.MCQ ? 'MCQ' : 'Complete'}
                    index={index}
                    onDelete={() => deleteQuestionAt(index)}
                    editAnswers={item.answers}
                  />
                );
              })}
            </Stack>
          )}

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              {questions.length} question{questions.length === 1 ? '' : 's'}
            </Typography>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              loading={isSubmitting}
              disabled={questions.length === 0}
              startIcon={<Iconify icon="eva:save-fill" />}
              sx={{ minWidth: 180 }}
            >
              Save quiz
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
